import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DepartmentService } from 'src/app/services/api/departments/department.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { DepartmentModel } from 'src/app/services/models/department.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  MakeupTeachingRequestReviewListParams,
  MakeupTeachingRequestReviewModel,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

type ReviewStatus = 'submitted' | 'approved' | 'rejected' | 'revision_requested' | 'draft' | 'unknown';
type ReviewTab = 'all' | 'pending' | 'approved' | 'rejected';
type StudyModeName = { name_th?: string | null; name_en?: string | null } | null | undefined;
type TeachingScheduleSlot = NonNullable<NonNullable<MakeupTeachingRequestReviewModel['teaching_schedule_summary']>['slots']>[number];

const THAI_WEEKDAY_NAMES: Record<number, string> = {
  0: 'วันอาทิตย์',
  1: 'วันจันทร์',
  2: 'วันอังคาร',
  3: 'วันพุธ',
  4: 'วันพฤหัสบดี',
  5: 'วันศุกร์',
  6: 'วันเสาร์',
  7: 'วันอาทิตย์',
};

function trimmedRequired(control: AbstractControl): ValidationErrors | null {
  return String(control.value ?? '').trim() === '' ? { required: true } : null;
}

interface ReviewRow {
  id: number;
  sequence?: number | string | null;
  academicTerm: string;
  year: string;
  teacherName: string;
  academicRank: string;
  department: string;
  submittedAt: string;
  status: ReviewStatus;
  statusLabel: string;
  canApprove: boolean;
  approvalBlockedReason: string | null;
  canReject: boolean;
  rejectionBlockedReason: string | null;
  raw: MakeupTeachingRequestReviewModel;
}

interface ReviewDataSource {
  data: ReviewRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-teach-compensation-review',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './teach-compensation-review.component.html',
  styleUrl: './teach-compensation-review.component.scss',
})
export class TeachCompensationReviewComponent implements OnInit {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;
  @ViewChild('approveConfirmDialog')
  approveConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('approveSuccessDialog')
  approveSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('rejectSuccessDialog')
  rejectSuccessDialog!: ConfirmDialogComponent;

  form!: FormGroup;
  rejectForm!: FormGroup;
  columns: any[] = [];
  datasource = signal<ReviewDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  approving = signal(false);
  rejecting = signal(false);
  actionError = signal<string | null>(null);
  selectedRow = signal<ReviewRow | null>(null);
  isDetailModalOpen = signal(false);
  isRejectModalOpen = signal(false);
  activeTab = signal<ReviewTab>('all');
  pageSizeOptions = [10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  departmentOptions: DepartmentModel[] = [];

  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    academicTerm: 'academic_term_name',
    year: 'academic_term_year',
    teacherName: 'teacher_name',
    department: 'department',
    submittedAt: 'submitted_at',
    status: 'status',
  };
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private departmentService = inject(DepartmentService);
  private teachCompensationService = inject(TeachCompensationService);
  private academicTerms: AcademicTermYearModel[] = [];

  ngOnInit(): void {
    this.initForm();
    this.initRejectForm();
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  onTabChange(tab: ReviewTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadReviews(1);
  }

  onSearch(): void {
    this.loadReviews(1);
  }

  onView(row: ReviewRow): void {
    this.actionError.set(null);
    this.selectedRow.set(row);
    this.isDetailModalOpen.set(true);
  }

  onCloseModal(): void {
    if (this.isActionProcessing()) {
      return;
    }

    this.isDetailModalOpen.set(false);
    this.selectedRow.set(null);
  }

  onOpenApproveConfirm(): void {
    if (this.isActionProcessing() || !this.canApprove(this.selectedRow())) {
      return;
    }

    this.approveConfirmDialog.open();
  }

  onConfirmApprove(): void {
    const row = this.selectedRow();

    if (row == null || !this.canApprove(row)) {
      return;
    }

    this.actionError.set(null);
    this.approving.set(true);
    this.teachCompensationService.approveMakeupTeachingRequestReview(row.id)
      .pipe(finalize(() => this.approving.set(false)))
      .subscribe({
        next: () => {
          this.approveConfirmDialog.close();
          this.isDetailModalOpen.set(false);
          this.selectedRow.set(null);
          this.loadReviews(this.currentPage);
          this.approveSuccessDialog.open();
        },
        error: (err) => {
          this.approveConfirmDialog.close();
          this.actionError.set(this.apiErrorMessage(err, 'ไม่สามารถรับรองคำขอสอนชดเชยได้'));
        },
      });
  }

  onOpenReject(): void {
    if (this.isActionProcessing() || !this.canReject(this.selectedRow())) {
      return;
    }

    this.actionError.set(null);
    this.rejectForm.reset({ comment: '' });
    this.isRejectModalOpen.set(true);
  }

  onCloseReject(): void {
    if (this.isActionProcessing()) {
      return;
    }

    this.isRejectModalOpen.set(false);
    this.rejectForm.reset({ comment: '' });
    this.actionError.set(null);
  }

  onConfirmReject(): void {
    const row = this.selectedRow();
    const comment = String(this.rejectForm.controls['comment'].value ?? '').trim();

    this.rejectForm.controls['comment'].setValue(comment);
    this.rejectForm.controls['comment'].markAsTouched();

    if (row == null || !this.canReject(row) || comment === '') {
      return;
    }

    this.actionError.set(null);
    this.rejecting.set(true);
    this.teachCompensationService.rejectMakeupTeachingRequestReview(row.id, comment)
      .pipe(finalize(() => this.rejecting.set(false)))
      .subscribe({
        next: () => {
          this.isRejectModalOpen.set(false);
          this.rejectForm.reset({ comment: '' });
          this.isDetailModalOpen.set(false);
          this.selectedRow.set(null);
          this.loadReviews(this.currentPage);
          this.rejectSuccessDialog.open();
        },
        error: (err) => {
          this.actionError.set(this.apiErrorMessage(err, 'ไม่สามารถไม่อนุมัติคำขอสอนชดเชยได้'));
        },
      });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadReviews(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadReviews(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadReviews(1);
  }

  canApprove(row: ReviewRow | null): boolean {
    return row?.canApprove === true;
  }

  canReject(row: ReviewRow | null): boolean {
    return row?.canReject === true;
  }

  getDisplayedBlockedReason(row: ReviewRow | null): string | null {
    return row?.approvalBlockedReason ?? row?.rejectionBlockedReason ?? null;
  }

  isActionProcessing(): boolean {
    return this.approving() || this.rejecting();
  }

  formatDetailDate(value?: string | null): string {
    return this.formatDate(value);
  }

  formatDetailTimeRange(start?: string | null, end?: string | null): string {
    return this.formatTimeRange(start, end);
  }

  getSelectedSubjectCode(row: ReviewRow | null): string {
    const item = row?.raw;
    return item?.subject?.code ??
      item?.subject_schedule_group?.subject?.code ??
      item?.subject_schedule_group?.code ??
      '-';
  }

  getSelectedSubjectName(row: ReviewRow | null): string {
    const item = row?.raw;
    return item?.subject?.name ??
      item?.subject_schedule_group?.subject?.name ??
      item?.subject_schedule_group?.name ??
      '-';
  }

  getSelectedCurriculum(row: ReviewRow | null): string {
    const item = row?.raw;
    return item?.subject?.curriculum_type?.name ??
      item?.subject_schedule_group?.subject?.curriculum_type?.name ??
      item?.subject_schedule_group?.curriculum_type?.name ??
      '-';
  }

  getSelectedStudyMode(row: ReviewRow | null): string {
    const item = row?.raw;
    const subjectMode = item?.subject?.study_mode;
    const groupSubjectMode = item?.subject_schedule_group?.subject?.study_mode;
    const groupMode = item?.subject_schedule_group?.study_mode;

    return this.firstPresent(
      this.getStudyModeName(subjectMode),
      this.getStudyModeName(groupSubjectMode),
      this.getStudyModeName(groupMode),
    );
  }

  getSelectedCourseUnit(row: ReviewRow | null): string {
    const item = row?.raw;
    return item?.subject?.courseunit ??
      item?.subject_schedule_group?.subject?.courseunit ??
      item?.subject_schedule_group?.courseunit ??
      '-';
  }

  getTeachingDays(row: ReviewRow | null): string {
    const dayNames = this.scheduleSlots(row)
      .map((slot) => THAI_WEEKDAY_NAMES[slot.day_of_week])
      .filter((name): name is string => name != null);

    return [...new Set(dayNames)].join(', ') || '-';
  }

  getTeachingTimes(row: ReviewRow | null): string {
    const ranges = this.scheduleSlots(row)
      .map((slot) => this.formatDetailTimeRange(slot.start_time, slot.end_time))
      .filter((range) => range !== '-');

    return [...new Set(ranges)].join(', ') || '-';
  }

  getTeachingHours(row: ReviewRow | null): string {
    const hours = row?.raw.teaching_schedule_summary?.teaching_hours;
    return typeof hours === 'number' && Number.isFinite(hours) ? hours.toFixed(2) : '-';
  }

  private scheduleSlots(row: ReviewRow | null): TeachingScheduleSlot[] {
    return row?.raw.teaching_schedule_summary?.slots ?? [];
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
      keywords: [''],
      departmentId: [null],
    });
  }

  private initRejectForm(): void {
    this.rejectForm = this.fb.group({
      comment: ['', trimmedRequired],
    });
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadReviews(1));
  }

  private loadFilterOptions(): void {
    forkJoin({
      terms: this.studyPeriodService.getAcademicTermYears().pipe(take(1)),
      departments: this.departmentService.getDepartments({ limit: 200 }).pipe(take(1)),
    }).subscribe({
      next: ({ terms, departments }) => {
        this.academicTerms = terms;
        const academicTermOptions = this.buildAcademicTermOptions(terms);
        const yearOptions = this.buildAcademicYearOptions(terms);

        this.academicTermOptions = academicTermOptions.length > 0 ? academicTermOptions : this.academicTermOptions;
        this.yearOptions = yearOptions.length > 0 ? yearOptions : this.yearOptions;
        this.departmentOptions = departments.data;
        this.ensureAcademicTermFilterValues();
        this.loadReviews(1);
      },
      error: (err) => {
        console.error(err);
        this.loadReviews(1);
      },
    });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
      },
      {
        prop: 'academicTerm',
        name: 'เทอมการศึกษา',
        sortable: false,
        width: 120,
        minWidth: 120,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: false,
        width: 120,
        minWidth: 120,
      },
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 210,
        minWidth: 180,
      },
      {
        prop: 'department',
        name: 'สาขาวิชา',
        sortable: true,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'submittedAt',
        name: 'วันที่ส่งคำขอ',
        sortable: true,
        width: 170,
        minWidth: 160,
      },
      {
        prop: 'status',
        name: 'สถานะ:',
        sortable: false,
        cellTemplate: this.statusTemplate,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        headerClass: 'text-center review-status-header',
        cellClass: 'text-center review-status-cell',
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center review-action-cell',
        headerClass: 'text-center review-action-header',
      },
    ];
  }

  private loadReviews(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);

    this.teachCompensationService.getMakeupTeachingRequestReviewsList({
      ...this.buildFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const rows = response.data.map((item) => this.mapRequestToRow(item));

        this.datasource.set(this.createDataSource(
          rows,
          response.page,
          response.limit,
          response.total,
          response.total_pages,
          (response.page - 1) * response.limit,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private createDataSource(
    rows: ReviewRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
    offset = (page - 1) * limit,
  ): ReviewDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private buildFilterParams(): MakeupTeachingRequestReviewListParams {
    const { academicTerm, year, keywords, departmentId } = this.form.getRawValue();
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const selectedTerm = this.findAcademicTerm(normalizedAcademicTerm, normalizedYear);
    const params: MakeupTeachingRequestReviewListParams = {};

    if (selectedTerm != null) {
      params.academic_term_id = selectedTerm.id;
    } else {
      if (normalizedAcademicTerm !== '') {
        params.academic_term_name = normalizedAcademicTerm;
      }

      const numericYear = Number(normalizedYear);
      if (Number.isFinite(numericYear) && numericYear > 0) {
        params.academic_term_year = this.toChristianYear(numericYear);
      }
    }

    const status = this.statusParamForTab(this.activeTab());
    if (status != null) {
      params.status = status;
    }

    const normalizedKeywords = String(keywords ?? '').trim();
    if (normalizedKeywords !== '') {
      params.keywords = normalizedKeywords;
    }

    if (departmentId != null && departmentId !== '') {
      params.department_id = departmentId;
    }

    return params;
  }

  private mapRequestToRow(item: MakeupTeachingRequestReviewModel): ReviewRow {
    const academicTerm = this.findAcademicTermById(item.schedule_submission?.academic_term_id ?? null);
    const status = this.normalizeStatus(item.status);
    const academicRank = this.getAcademicRankName(item);
    const department = this.getDepartmentName(item);

    return {
      id: item.id,
      sequence: item.sequence,
      academicTerm: academicTerm?.name ?? '-',
      year: academicTerm != null ? String(this.toBuddhistYear(academicTerm.year)) : '-',
      teacherName: this.getTeacherDisplayName(item, academicRank),
      academicRank,
      department,
      submittedAt: this.formatDateTime(item.submitted_at ?? item.updated_at),
      status,
      statusLabel: this.getStatusLabel(status),
      canApprove: status === 'submitted' && item.can_approve !== false,
      approvalBlockedReason: item.approval_blocked_reason ?? null,
      canReject: status === 'submitted' && (item.can_reject ?? item.can_cancel) === true,
      rejectionBlockedReason: item.rejection_blocked_reason ?? item.cancellation_blocked_reason ?? null,
      raw: item,
    };
  }

  private getTeacherDisplayName(item: MakeupTeachingRequestReviewModel, academicRank: string): string {
    const teacherName = item.teacher?.full_name ?? '-';

    return academicRank !== '-' ? `${academicRank} ${teacherName}` : teacherName;
  }

  private getAcademicRankName(item: MakeupTeachingRequestReviewModel): string {
    return this.firstPresent(
      item.academic_rank?.name,
      item.teacher?.academic_rank?.name,
    );
  }

  private getDepartmentName(item: MakeupTeachingRequestReviewModel): string {
    return this.firstPresent(
      item.department?.name_th,
      item.department?.name,
      item.teacher?.department?.name_th,
      item.teacher?.department?.name,
    );
  }

  private getStudyModeName(studyMode: StudyModeName): string | null | undefined {
    return studyMode?.name_th ?? studyMode?.name_en;
  }

  private firstPresent(...values: (string | null | undefined)[]): string {
    const value = values.find((item) => item != null && item !== '');

    return value ?? '-';
  }

  private statusParamForTab(tab: ReviewTab): string | null {
    if (tab === 'pending') {
      return 'submitted';
    }

    if (tab === 'approved') {
      return 'approved';
    }

    if (tab === 'rejected') {
      return 'rejected';
    }

    return null;
  }

  private normalizeStatus(status?: string | null): ReviewStatus {
    if (status === 'submitted' || status === 'approved' || status === 'rejected' || status === 'revision_requested' || status === 'draft') {
      return status;
    }

    return 'unknown';
  }

  private getStatusLabel(status: ReviewStatus): string {
    const labels: Record<ReviewStatus, string> = {
      submitted: 'รอดำเนินการ',
      approved: 'อนุมัติแล้ว',
      rejected: 'ไม่อนุมัติ',
      revision_requested: 'ส่งกลับแก้ไข',
      draft: 'แบบร่าง',
      unknown: 'ไม่ทราบสถานะ',
    };

    return labels[status];
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof HttpErrorResponse)) {
      return fallback;
    }

    const payload: unknown = error.error;
    if (typeof payload !== 'object' || payload == null) {
      return fallback;
    }

    const response = payload as Record<string, unknown>;
    const errors = response['errors'];
    if (Array.isArray(errors)) {
      const message = errors.find((item): item is string => typeof item === 'string' && item.trim() !== '');
      if (message != null) {
        return message;
      }
    }

    const singleError = response['error'];
    return typeof singleError === 'string' && singleError.trim() !== ''
      ? singleError
      : fallback;
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === academicTermName &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private findAcademicTermById(id: number | null): AcademicTermYearModel | undefined {
    if (id == null) {
      return undefined;
    }

    return this.academicTerms.find((term) => term.id === id);
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const currentYear = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private formatDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return `${this.formatDate(value)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
  }

  private formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = this.toBuddhistYear(date.getFullYear());

    return `${day}/${month}/${year}`;
  }

  private formatTimeRange(start?: string | null, end?: string | null): string {
    const startTime = this.formatTime(start);
    const endTime = this.formatTime(end);

    if (startTime === '-' || endTime === '-') {
      return '-';
    }

    return `${startTime} - ${endTime} น.`;
  }

  private formatTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const timeMatch = /T(\d{2}):(\d{2})/.exec(value);
    if (timeMatch != null) {
      return `${Number(timeMatch[1])}:${timeMatch[2]}`;
    }

    const timeParts = value.split(':');
    if (timeParts.length < 2) {
      return value;
    }

    const [hours, minutes] = timeParts;

    return `${Number(hours)}:${minutes}`;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private toChristianYear(year: number): number {
    return year >= 2400 ? year - 543 : year;
  }
}
